// src/components/upmode-agent/chat-message.tsx
import React from "react";
import { User } from "lucide-react";
import { UpmodeMessage } from "@/app/services/upmode-agent/upmode-agent-api";
import { Icon } from "../kit";
import { Orb, ToolStep } from "@/app/components/shared/advanced-vocabulary";

interface ChatMessageProps {
  message: UpmodeMessage;
  streaming?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Markdown renderer — supports headings, lists, code fences, inline formatting,
// links, AND GitHub-style tables.
// ────────────────────────────────────────────────────────────────────────────

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith("|")) trimmed = trimmed.slice(1);
  if (trimmed.endsWith("|")) trimmed = trimmed.slice(0, -1);
  return trimmed.split("|").map(c => c.trim());
}

function parseAlignment(separator: string): Array<"left" | "center" | "right"> {
  return splitTableRow(separator).map(cell => {
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });
}

function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Fenced code block ────────────────────────────────────────────────
    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]); i++;
      }
      i++;
      elements.push(
        <pre key={key++} className="upm-md-code">
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // ── GitHub-style tables ──────────────────────────────────────────────
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const headerCells = splitTableRow(line);
      const alignments = parseAlignment(lines[i + 1]);
      i += 2;

      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        bodyRows.push(splitTableRow(lines[i]));
        i++;
      }

      elements.push(
        <div key={key++} className="upm-md-tablewrap">
          <table className="upm-md-table">
            <thead>
              <tr>
                {headerCells.map((h, idx) => (
                  <th key={idx} style={{ textAlign: alignments[idx] || "left" }}>
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} style={{ textAlign: alignments[cIdx] || "left" }}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // ── Headings ─────────────────────────────────────────────────────────
    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const content = headingMatch[2];
      elements.push(
        <div key={key++} className={`upm-md-h upm-md-h--${level}`}>
          {renderInline(content)}
        </div>
      );
      i++;
      continue;
    }

    // ── Bulleted list ────────────────────────────────────────────────────
    if (/^[-*]\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(<li key={key++}>{renderInline(lines[i].replace(/^[-*]\s+/, ""))}</li>);
        i++;
      }
      elements.push(
        <ul key={key++} className="upm-md-ul">{items}</ul>
      );
      continue;
    }

    // ── Numbered list ────────────────────────────────────────────────────
    if (/^\d+\.\s+/.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(<li key={key++}>{renderInline(lines[i].replace(/^\d+\.\s+/, ""))}</li>);
        i++;
      }
      elements.push(
        <ol key={key++} className="upm-md-ol">{items}</ol>
      );
      continue;
    }

    // ── Blank line ───────────────────────────────────────────────────────
    if (line.trim() === "") {
      elements.push(<div key={key++} className="upm-md-gap" />);
      i++;
      continue;
    }

    // ── Regular paragraph ────────────────────────────────────────────────
    elements.push(
      <p key={key++} className="upm-md-p">{renderInline(line)}</p>
    );
    i++;
  }

  return elements;
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const code = remaining.match(/`([^`]+)`/);
    const bold = remaining.match(/\*\*([^*]+)\*\*/);
    const italic = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
    const link = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);

    const candidates = [
      code && { type: "code", match: code, start: remaining.indexOf(code[0]) },
      bold && { type: "bold", match: bold, start: remaining.indexOf(bold[0]) },
      italic && { type: "italic", match: italic, start: remaining.indexOf(italic[0]) },
      link && { type: "link", match: link, start: remaining.indexOf(link[0]) },
    ].filter(Boolean) as Array<{ type: string; match: RegExpMatchArray; start: number }>;

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const earliest = candidates.reduce((a, b) => (a.start < b.start ? a : b));
    if (earliest.start > 0) parts.push(remaining.slice(0, earliest.start));

    const m = earliest.match;
    if (earliest.type === "code") {
      parts.push(<code key={key++} className="upm-md-inline">{m[1]}</code>);
    } else if (earliest.type === "bold") {
      parts.push(<strong key={key++}>{m[1]}</strong>);
    } else if (earliest.type === "italic") {
      parts.push(<em key={key++}>{m[1]}</em>);
    } else if (earliest.type === "link") {
      parts.push(<a key={key++} className="upm-md-link" href={m[2]} target="_blank" rel="noreferrer">{m[1]}</a>);
    }

    remaining = remaining.slice(earliest.start + m[0].length);
  }

  return <>{parts}</>;
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export function ChatMessage({ message, streaming }: ChatMessageProps) {
  const isUser = message.role === "user";
  const isAssistant = message.role === "assistant";

  const hasContent = !!(message.content && message.content.trim());
  const hasToolCalls = message.tool_calls && message.tool_calls.length > 0;

  if (isAssistant && !hasContent && !hasToolCalls) return null;

  // A historical intermediate turn: tool calls and no text. It renders as the
  // same tool-step chips a live run draws, so the transcript of a finished
  // conversation and the conversation as it happened look like one thing.
  if (isAssistant && !hasContent && hasToolCalls) {
    return (
      <div className="upm-msg upm-msg--agent">
        <Orb size="xs" />
        <div className="upm-msg__body">
          <div className="upm-toolsteps">
            {message.tool_calls.map((tc, i) => (
              <ToolStep key={i} running={false} label={<>Called <strong>{tc.name}</strong></>} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`upm-msg ${isUser ? "upm-msg--user" : "upm-msg--agent"}`}>
      {/* The assistant wears the orb; the human wears a teal mark, because teal
          is the human's own signal everywhere else in this product. The agent's
          `avatar_emoji` is untouched in the payload and simply not rendered. */}
      {isUser ? (
        <span className="upm-msg__avatar">
          <Icon icon={User} size="sm" />
        </span>
      ) : (
        <Orb size="xs" />
      )}

      <div className="upm-msg__body">
        <div className="upm-msg__bubble">
          {isAssistant ? renderMarkdown(message.content || "") : message.content}
          {streaming && <span className="upm-msg__caret" aria-hidden="true" />}
        </div>
      </div>
    </div>
  );
}
